import { z } from "zod";
import { activityRuleSchema } from "./activity-filter";

export const recordRuleSchema = z.union([
  activityRuleSchema,
  z.object({
    field: z.enum(["company", "tags"]),
    operator: z.enum(["is", "is_not"]),
    values: z.array(z.uuid()).min(1),
  }),
  z.object({
    field: z.literal("domain"),
    operator: z.enum(["contains", "not_contains"]),
    value: z.string().trim().min(1),
  }),
]);
export type RecordRule = z.infer<typeof recordRuleSchema>;
export type RecordFilterGroup = { operator: "and" | "or"; rules: RecordFilterNode[] };
export type RecordFilterNode = RecordRule | RecordFilterGroup;
export const recordFilterGroupSchema: z.ZodType<RecordFilterGroup> = z.object({
  operator: z.enum(["and", "or"]),
  rules: z.array(z.lazy(() => z.union([recordRuleSchema, recordFilterGroupSchema]))).min(1),
});
export const directoryFields = {
  person: ["status", "company", "tags", "assignee", "date", "text"],
  company: ["domain", "tags", "assignee", "date", "text"],
} as const;
export function directoryFilterSchema(kind: keyof typeof directoryFields) {
  return recordFilterGroupSchema.superRefine((group, ctx) => {
    const check = (node: RecordFilterNode) => {
      if ("rules" in node) node.rules.forEach(check);
      else if (!(directoryFields[kind] as readonly string[]).includes(node.field))
        ctx.addIssue({ code: "custom", message: `Unsupported ${kind} filter field.` });
    };
    check(group);
  });
}
export function directoryFilterQuerySchema(kind: keyof typeof directoryFields) {
  return z
    .string()
    .transform((value, ctx) => {
      try {
        return JSON.parse(value) as unknown;
      } catch {
        ctx.addIssue({ code: "custom", message: "Invalid filter JSON." });
        return z.NEVER;
      }
    })
    .pipe(directoryFilterSchema(kind));
}
export function directoryFilterFromQuery(
  kind: keyof typeof directoryFields,
  query: Record<string, string | undefined>,
): RecordFilterGroup {
  const rules: RecordFilterNode[] = [];
  if (query.filter) rules.push(directoryFilterQuerySchema(kind).parse(query.filter));
  for (const [key, field] of [
    ["state", "status"],
    ["company", "company"],
    ["tags", "tags"],
    ["assignee", "assignee"],
  ] as const) {
    if (query[key] && (kind === "person" || !["state", "company"].includes(key)))
      rules.push(recordRuleSchema.parse({ field, operator: "is", values: query[key]?.split(",") }));
  }
  if (kind === "company" && query.domain)
    rules.push({ field: "domain", operator: "contains", value: query.domain });
  const first = rules[0];
  return rules.length === 1 && first && "rules" in first ? first : { operator: "and", rules };
}
export function recordFilterCount(node: RecordFilterNode): number {
  return "rules" in node
    ? node.rules.reduce((count, child) => count + recordFilterCount(child), 0)
    : 1;
}
